import {authApi} from '@/api/auth.api.js'
import {joinPaths, openUrl} from '@/helpers/url.js'
import {useTeams} from '@/composables/use-teams.js'
import {useEnv} from '@/composables/use-env.js'
import {useRailsUrls} from '@/composables/use-rails-urls.js'
import {ref} from 'vue'

//Global refs
const isAuthenticated = ref(false)
const bearerToken = ref(null)
const csrfToken = ref(null)
const authPromise = ref(null)

//Flag to check if already redirecting to login page
const isRedirecting = ref(false)

/**
 * Authentication composable
 */
export function useAuthentication() {

  //Teams composable
  const {hasInitializedTeams, isInTeams, getTeamsAuthToken, userEmail} = useTeams()
  const {railsAppRootUrl} = useRailsUrls()
  const {href} = useEnv()

  //Process auth data
  const processAuthData = json => {

    //Store CSRF token and mark as authenticated
    bearerToken.value = json.token
    isAuthenticated.value = true
  }

  //Authenticate through Teams
  const authenticateWithTeams = async () => {

    //Must have initialize Teams before calling this
    if (!hasInitializedTeams.value) {
      throw new Error('Please call `initializeTeams()` first before trying to authenticate with Teams')
    }

    //Get token and authenticate
    const token = await getTeamsAuthToken()
    const json = await authApi.authenticateWithTeamsSSOToken(token, {
      userEmail: userEmail.value,
    })

    //Process auth data
    processAuthData(json)
  }

  //Authenticate with redirect
  const authenticateWithRedirect = () => {

    //Already redirecting
    if (isRedirecting.value) {
      return
    }

    //Set flag
    isRedirecting.value = true

    //Determine login and redirect URLs
    const loginUrl = joinPaths(railsAppRootUrl.value, 'login')
    const redirectUrl = encodeURIComponent(href.value)
    console.warn('Redirecting to login page with redirect URL:', redirectUrl)

    //Redirect
    openUrl(loginUrl, {redirect_url: redirectUrl})
  }

  //Authenticate
  const authenticate = async () => {

    //Already authenticating
    if (authPromise.value) {
      return authPromise.value
    }

    //Promise handler
    const fn = async (resolve, reject) => {
      try {
        if (isInTeams.value) {
          await authenticateWithTeams()
        }
        else {
          authenticateWithRedirect()
        }
        resolve()
      }
      catch (error) {
        reject(error)
      }
    }

    //Set and return promise
    return authPromise.value = new Promise(fn)
  }

  //Set authenticated
  const setAuthenticated = value => isAuthenticated.value = value

  //Obtain CSRF token
  const obtainCsrfToken = async () => {
    csrfToken.value = await authApi.getCsrfToken()
  }

  //Expose interface
  return {
    authenticate,
    authenticateWithTeams,
    authenticateWithRedirect,
    setAuthenticated,
    obtainCsrfToken,
    isAuthenticated,
    bearerToken,
    csrfToken,
  }
}
